import { defineStore } from 'pinia';
import { RequestRoadsideAssistanceDetailsFleet } from '@/interfaces/pages/request-roadside-assistance.interface';
import { ref } from 'vue';
import { useRoadsideAssistanceBase } from './request-roadside-assistance';
import { RoadsideAssistanceService as _roadsideAssistanceService } from '@/services/store/roadside-assistance.service';
import { FleetServiceModel, FleetServiceState } from '@/interfaces/entities/fleet-service-model.interface';
import { useFormStore } from '../form/formStore';
import {
  createRsaJobHtmlRequest,
  YOUR_DETAILS_VEHICLE_DETAILS_STEP_ID as VEHICLE_DETAILS_STEP_ID,
} from '@/forms/request-roadside-assistance-fleet/mappings/request-roadside-assistance-mappings';

export const useRequestRoadsideAssistanceFleetStore = defineStore('RequestRoadsideAssistanceFleet', () => {
  const formStore = useFormStore();

  // State
  const requestRoadsideAssistanceDetails = ref<RequestRoadsideAssistanceDetailsFleet>({
    registrationNumber: '',
    driverName: '',
    phoneNumber: '',
    serviceNumber: null,
    breakdownLocationLatitude: 0,
    breakdownLocationLongitude: 0,
    breakdownLocationAddress: '',
    breakdownCause: '',
    comments: '',
    vehicle: null,
  });

  const fleetServiceModel = ref<FleetServiceModel | null>(null);

  // Inherit functions from the base roadsideassistance
  const {
    setBreakdownLocation,
    setComments,
    clearComments,
    setVehicleDetails,
    checkAndAdjustStep,
    rsaTrackingId,
    dispatchGroup,
    isTrackable,
  } = useRoadsideAssistanceBase(requestRoadsideAssistanceDetails.value);

  // Actions

  const createRsaJobHtml = async () => {
    try {
      const rsaRequest = await createRsaJobHtmlRequest(requestRoadsideAssistanceDetails.value);
      rsaRequest.comments?.push(`DRIVER:${requestRoadsideAssistanceDetails.value.driverName}`);
      const result = await _roadsideAssistanceService.createRsaJobHtml(rsaRequest, true);

      if (result?.jobId) {
        // Set the trackingId if successful
        rsaTrackingId.value = result.jobId;
        dispatchGroup.value = result.roadsideAssistanceStatus?.dispatchGroup;
      }
      return result;
    } catch {
      return null;
    }
  };

  function setYourDetails(
    registrationNumber: string,
    driverName: string,
    phoneNumber: string,
    serviceNumber: string | null
  ) {
    requestRoadsideAssistanceDetails.value.registrationNumber = registrationNumber;
    requestRoadsideAssistanceDetails.value.driverName = driverName;
    requestRoadsideAssistanceDetails.value.phoneNumber = phoneNumber;
    requestRoadsideAssistanceDetails.value.serviceNumber = serviceNumber;
  }

  async function getFleetServiceModel(): Promise<FleetServiceModel | null> {
    const requestRsaDetails = requestRoadsideAssistanceDetails.value;

    try {
      const response = await _roadsideAssistanceService.getFleetServiceByRegoAndServiceNumber(
        requestRsaDetails.registrationNumber,
        requestRsaDetails.serviceNumber || null
      );
      fleetServiceModel.value = response;
      if (response) {
        requestRoadsideAssistanceDetails.value.serviceNumber = response.serviceNumber;
        requestRoadsideAssistanceDetails.value.batteries = response.batteries || '';
      }

      return response;
    } catch (error) {
      console.error('Error validating details:', error);
      fleetServiceModel.value = null;
      return null;
    }
  }

  /*
   * Add/ remove steps
   */
  const adjustSteps = async () => {
    if (fleetServiceModel.value?.serviceState === FleetServiceState.RequireVehicleInfo) {
      // Add vehicle step
      await checkAndAdjustStep(VEHICLE_DETAILS_STEP_ID, true, formStore.currentStepIndex + 1);
    } else {
      // Remove vehicle step
      await checkAndAdjustStep(VEHICLE_DETAILS_STEP_ID);
    }
  };

  return {
    requestRoadsideAssistanceDetails,
    setBreakdownLocation,
    setComments,
    clearComments,
    createRsaJobHtml,
    setYourDetails,
    setVehicleDetails,
    getFleetServiceModel,
    adjustSteps,
    rsaTrackingId,
    dispatchGroup,
    isTrackable,
  };
});
